import jsPDF from "jspdf";
import logoUrl from "@project/assets/jivandhara-logo-bg.png";

export type DonationReceiptTemplateData = {
  /** Title band text; defaults to "DONATION RECEIPT" */
  title?: string;
  receiptNo: string;
  date: string;
  donationScheme?: string;
  donorName: string;
  donorMobile?: string;
  donorEmail?: string;
  donorAddress?: string;
  donorPan?: string;
  organization?: string;
  amount: number;
  donationType?: string;
  paymentMode?: string;
  cashOrChequeNo?: string;
  remarks?: string;
  /** Bhojanshala-specific: preferred Jain calendar tithi */
  preferredTithi?: string;
  /** Bhojanshala-specific: occasion for the sponsorship */
  occasion?: string;
};

/** Legacy shape used by donor account / thank-you pages */
export type ReceiptData = {
  receipt_no: string;
  donation_date: string;
  amount: number;
  payment_mode?: string | null;
  transaction_id?: string | null;
  purpose?: string | null;
  category?: string | null;
  requires_80g?: boolean | null;
  donor: {
    full_name?: string | null;
    mobile?: string | null;
    email?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    pincode?: string | null;
    pan?: string | null;
    organization?: string | null;
  } | null;
};

const ORG = {
  name: "Jivandhara Old Age Home",
  address: "Jivandhara vruddhashram, Lambha",
  email: "jivandharavrudhashram@gmail.com",
  phone: "—",
  pan: "AAATM8600J",
} as const;

/** App primary (~ `--primary` / logo blue disc #1d4a8a) */
const PRIMARY: [number, number, number] = [29, 74, 138];
const BEIGE: [number, number, number] = [248, 244, 236];
const NOTE_BG: [number, number, number] = [232, 244, 252];
const NOTE_BORDER: [number, number, number] = [147, 197, 253];
/** Middle-section label / title color (reference dark navy) */
const LABEL: [number, number, number] = [26, 32, 44];
/** Middle-section value color (reference muted grey-blue) */
const VALUE: [number, number, number] = [74, 85, 104];
/** Amount figure stays app primary (do not use muted value grey) */
const AMOUNT: [number, number, number] = PRIMARY;
const AMOUNT_CSS = "rgb(29,74,138)";

const W = 210;
const H = 297;
const MARGIN = 14;
const CONTENT_W = W - MARGIN * 2;
const FOOTER_H = 12;
const HEADER_H = 40;
const TITLE_BAND_H = 10;

function formatReceiptDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value || "—";
  }
  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatAmountDigits(amount: number): string {
  const n = Number.isFinite(amount) ? amount : 0;
  return n.toLocaleString("en-IN");
}

/** Helvetica has no ₹ glyph — render it via canvas so the PDF shows the real symbol. */
function rupeeSymbolPng(
  fontSizePx: number,
  colorCss: string,
  bold = false
): { dataUrl: string; widthMm: number; heightMm: number } | null {
  if (typeof document === "undefined") {
    return null;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  const font = `${bold ? "bold " : ""}${fontSizePx}px Arial, "Noto Sans", sans-serif`;
  ctx.font = font;
  const measured = ctx.measureText("₹");
  const width = Math.ceil(measured.width) + 6;
  const height = Math.ceil(fontSizePx * 1.35);
  canvas.width = width;
  canvas.height = height;
  ctx.font = font;
  ctx.fillStyle = colorCss;
  ctx.textBaseline = "alphabetic";
  ctx.fillText("₹", 2, fontSizePx);
  const pxToMm = 25.4 / 96;
  return {
    dataUrl: canvas.toDataURL("image/png"),
    widthMm: width * pxToMm,
    heightMm: height * pxToMm,
  };
}

function drawAmountWithRupee(
  doc: jsPDF,
  amount: number,
  x: number,
  labelY: number
) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...LABEL);
  doc.text("Amount (", x, labelY);

  const labelRupee = rupeeSymbolPng(13, "rgb(26,32,44)", true);
  let afterLabelX = x + doc.getTextWidth("Amount (");
  if (labelRupee) {
    // Align symbol with label baseline
    doc.addImage(
      labelRupee.dataUrl,
      "PNG",
      afterLabelX,
      labelY - labelRupee.heightMm + 1.2,
      labelRupee.widthMm,
      labelRupee.heightMm
    );
    afterLabelX += labelRupee.widthMm + 0.4;
  } else {
    doc.text("Rs.", afterLabelX, labelY);
    afterLabelX += doc.getTextWidth("Rs.") + 0.4;
  }
  doc.text(")", afterLabelX, labelY);

  const valueY = labelY + 9;
  const valueRupee = rupeeSymbolPng(22, AMOUNT_CSS, true);
  let valueX = x;
  if (valueRupee) {
    doc.addImage(
      valueRupee.dataUrl,
      "PNG",
      valueX,
      valueY - valueRupee.heightMm + 1.6,
      valueRupee.widthMm,
      valueRupee.heightMm
    );
    valueX += valueRupee.widthMm + 1.2;
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(AMOUNT[0], AMOUNT[1], AMOUNT[2]);
    doc.text("Rs.", valueX, valueY);
    valueX += doc.getTextWidth("Rs.") + 1.2;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(AMOUNT[0], AMOUNT[1], AMOUNT[2]);
  doc.text(formatAmountDigits(amount), valueX, valueY);
}

function display(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "—";
  }
  return trimmed;
}

function drawLine(doc: jsPDF, y: number) {
  doc.setDrawColor(210, 210, 210);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, W - MARGIN, y);
}

function drawSectionTitle(doc: jsPDF, title: string, y: number): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...LABEL);
  doc.text(title, MARGIN, y);
  drawLine(doc, y + 2);
  return y + 8;
}

function drawField(
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  width: number
): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...LABEL);
  doc.text(label, x, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...VALUE);
  const lines = doc.splitTextToSize(display(value), width);
  doc.text(lines, x, y + 4.5);
  return y + 4.5 + lines.length * 4.2 + 3;
}

function addPageLogoWatermark(doc: jsPDF) {
  /** Large centered cover watermark behind body content */
  const logoSize = 130;
  const x = (W - logoSize) / 2;
  const y = 78;
  try {
    const gState = (
      doc as jsPDF & { GState?: new (args: { opacity: number }) => unknown }
    ).GState;
    if (gState) {
      doc.setGState(new gState({ opacity: 0.1 }));
    }
    doc.addImage(logoUrl, "PNG", x, y, logoSize, logoSize);
    if (gState) {
      doc.setGState(new gState({ opacity: 1 }));
    }
  } catch {
    // Logo is decorative; receipt still downloads without it.
  }
}

export function renderDonationReceiptPdf(
  data: DonationReceiptTemplateData
): jsPDF {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // Header — application primary color, full width
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, W, HEADER_H, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(ORG.name, W / 2, 11, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(`Address : ${ORG.address}`, W / 2, 17, { align: "center" });
  doc.text(`Email : ${ORG.email}`, W / 2, 21.5, { align: "center" });
  doc.text(`Phone : ${ORG.phone}`, W / 2, 26, { align: "center" });
  doc.text(`PAN : ${ORG.pan}`, W / 2, 30.5, { align: "center" });

  // Title band — full page width (no left/right inset)
  const titleY = HEADER_H;
  doc.setFillColor(...BEIGE);
  doc.rect(0, titleY, W, TITLE_BAND_H, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.text(data.title?.trim() ?? "DONATION RECEIPT", W / 2, titleY + 6.8, {
    align: "center",
  });

  // Large logo watermark behind content
  addPageLogoWatermark(doc);

  let y = titleY + TITLE_BAND_H + 16;
  const colW = CONTENT_W / 3;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...LABEL);
  doc.text("Receipt No.", MARGIN, y);
  doc.text("Donation Scheme", MARGIN + colW + colW / 2, y, { align: "center" });
  doc.text("Date", W - MARGIN, y, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...VALUE);
  doc.text(display(data.receiptNo), MARGIN, y + 5);
  const schemeLines = doc.splitTextToSize(
    display(data.donationScheme),
    colW - 4
  );
  doc.text(schemeLines, MARGIN + colW + colW / 2, y + 5, { align: "center" });
  doc.text(formatReceiptDate(data.date), W - MARGIN, y + 5, {
    align: "right",
  });

  y += 8 + Math.max(schemeLines.length, 1) * 4.5 + 10;
  y = drawSectionTitle(doc, "Donor Information", y);

  const col1 = MARGIN;
  const col2 = MARGIN + colW;
  const col3 = MARGIN + colW * 2;
  const fieldW = colW - 4;

  // Draw donor fields row-by-row so the last row stays horizontally aligned
  const donorRows: [string, string, number][][] = [
    [
      ["Donor Name", data.donorName, col1],
      ["Phone", data.donorMobile ?? "", col2],
      ["Email", data.donorEmail ?? "", col3],
    ],
    [
      ["Address", data.donorAddress ?? "", col1],
      [
        data.title?.toLowerCase().includes("tiffin")
          ? "Scheme Price"
          : "Preferred Tithi",
        data.preferredTithi ?? "",
        col2,
      ],
      [
        data.title?.toLowerCase().includes("tiffin")
          ? "Times (Multiplier)"
          : "Occasion",
        data.occasion ?? "",
        col3,
      ],
    ],
  ];

  const ROW_GAP = 5;
  for (const row of donorRows) {
    const rowTop = y;
    const bottoms = row.map(([label, value, x]) =>
      drawField(doc, label, value, x, rowTop, fieldW)
    );
    y = Math.max(...bottoms) + ROW_GAP;
  }
  y -= ROW_GAP; // remove trailing gap after last row

  y += 6;
  y = drawSectionTitle(doc, "Donation Details", y);

  // Amount — real ₹ symbol + formatted digits
  drawAmountWithRupee(doc, data.amount, MARGIN, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...LABEL);
  doc.text("Payment Mode", MARGIN, y + 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...VALUE);
  doc.text(display(data.paymentMode), MARGIN, y + 24);

  // Signatory + note sit above the fixed bottom footer
  const footerTop = H - FOOTER_H;
  const noteH = 16;
  const noteY = footerTop - noteH - 8;
  const signY = noteY - 20;

  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.4);
  doc.line(W - MARGIN - 52, signY, W - MARGIN, signY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  doc.text("Authorized Signatory", W - MARGIN - 26, signY + 5, {
    align: "center",
  });
  doc.text(ORG.name, W - MARGIN - 26, signY + 10, { align: "center" });

  doc.setFillColor(...NOTE_BG);
  doc.setDrawColor(...NOTE_BORDER);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, noteY, CONTENT_W, noteH, 2, 2, "FD");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(30, 64, 110);
  doc.text(
    "Note : This donation is eligible for tax exemption under Section 80G of the Income Tax Act, 1961.",
    MARGIN + 4,
    noteY + 6,
    { maxWidth: CONTENT_W - 8 }
  );
  doc.text(
    "Please retain this receipt for your tax filing purposes.",
    MARGIN + 4,
    noteY + 11.5,
    { maxWidth: CONTENT_W - 8 }
  );

  // Bottom thank-you bar — full width, same beige as title band
  doc.setFillColor(...BEIGE);
  doc.rect(0, footerTop, W, FOOTER_H, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text("Thank you for your generous contribution!", W / 2, footerTop + 7.5, {
    align: "center",
  });

  return doc;
}

export function mapLegacyReceiptData(
  data: ReceiptData
): DonationReceiptTemplateData {
  const donor = data.donor;
  const address = [donor?.address, donor?.city, donor?.state, donor?.pincode]
    .filter(Boolean)
    .join(", ");
  const trimmedName = donor?.full_name?.trim();
  let donorName = "Anonymous Donor";
  if (trimmedName) {
    donorName = trimmedName;
  }

  return {
    receiptNo: data.receipt_no,
    date: data.donation_date,
    donationScheme: data.purpose ?? data.category ?? "—",
    donorName,
    donorMobile: donor?.mobile ?? "",
    donorEmail: donor?.email ?? "",
    donorAddress: address,
    donorPan: donor?.pan ?? "",
    organization: donor?.organization ?? "",
    amount: data.amount,
    donationType: data.category ?? data.purpose ?? "—",
    paymentMode: data.payment_mode ?? "—",
    cashOrChequeNo: data.transaction_id ?? "",
    remarks: "—",
  };
}

export function generateReceiptPDF(data: ReceiptData): jsPDF {
  return renderDonationReceiptPdf(mapLegacyReceiptData(data));
}

export function downloadReceiptPDF(data: ReceiptData) {
  const doc = generateReceiptPDF(data);
  doc.save(`Receipt-${data.receipt_no}.pdf`);
}

export function downloadDonationReceiptPdf(data: DonationReceiptTemplateData) {
  const doc = renderDonationReceiptPdf(data);
  const fileName =
    data.receiptNo && data.receiptNo !== "—"
      ? `Receipt-${data.receiptNo}.pdf`
      : "Donation-Receipt.pdf";
  doc.save(fileName);
}
